<!-- B10 机构数据看板（复用 A16 口径缩版 + 机构隔离） -->
<template>
  <div class="o2o-merchant-dashboard">
    <el-card shadow="never" class="mb-16px">
      <div class="header-row">
        <div>
          <h2 class="title">机构数据看板</h2>
          <p class="subtitle">本机构经营概况 · 近 7 日趋势 · 员工绩效</p>
        </div>
        <el-button :loading="loading" @click="loadAll">
          <Icon class="mr-4px" icon="ep:refresh" />刷新
        </el-button>
      </div>
    </el-card>

    <el-row :gutter="16" class="mb-16px">
      <el-col v-for="card in statCards" :key="card.key" :lg="6" :md="8" :sm="12" :xs="24" class="mb-16px">
        <el-card class="stat-card" shadow="hover" @click="goTo(card.path)">
          <div class="stat-content">
            <div class="stat-icon" :style="{ background: card.bg }">
              <Icon :icon="card.icon" :size="26" :style="{ color: card.color }" />
            </div>
            <div class="stat-info">
              <div class="stat-value">
                <el-skeleton v-if="loading" :rows="1" animated />
                <span v-else>{{ card.value }}</span>
              </div>
              <div class="stat-label">{{ card.title }}</div>
              <div v-if="card.extra" class="stat-extra">{{ card.extra }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="mb-16px">
      <el-col :span="6"><el-card><el-statistic title="今日开放容量" :value="availability.capacity || 0" /></el-card></el-col>
      <el-col :span="6"><el-card><el-statistic title="今日空档率" :value="availability.idleRate || 0" suffix="%" :precision="1" /></el-card></el-col>
      <el-col :span="6"><el-card><el-statistic title="已满/关闭时段" :value="availability.unavailableSlotCount || 0" /></el-card></el-col>
      <el-col :span="6"><el-card><el-statistic title="SLA 超时待派" :value="availability.slaTimeoutCount || 0" /></el-card></el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :lg="14" :md="24" :xs="24" class="mb-16px">
        <el-card shadow="never">
          <template #header><span>近 7 日收入趋势</span></template>
          <Echart :height="340" :options="trendChartOptions" />
        </el-card>
      </el-col>
      <el-col :lg="10" :md="24" :xs="24" class="mb-16px">
        <el-card shadow="never">
          <template #header><span>员工绩效排名</span></template>
          <el-table v-loading="loading" :data="providerRank" empty-text="暂无完成单数据" size="small" stripe>
            <el-table-column align="center" label="#" type="index" width="48" />
            <el-table-column label="师傅" min-width="90" prop="providerName" />
            <el-table-column align="center" label="完成单" prop="completedCount" width="72" />
            <el-table-column align="right" label="GMV" width="88">
              <template #default="{ row }">¥{{ fmtMoney(row.completedGmv) }}</template>
            </el-table-column>
            <el-table-column align="center" label="评分" width="72">
              <template #default="{ row }">{{ fmtScore(row.avgReviewScore) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import Echart from '@/components/Echart/src/Echart.vue'
import {
  getO2oMerchantDashboardProviderRank,
  getO2oMerchantAvailabilityStats,
  getO2oMerchantDashboardSummary,
  getO2oMerchantDashboardTrend,
  type O2oMerchantDashboardProviderRankVO,
  type O2oMerchantDashboardSummaryVO
} from '@/api/merchant/o2o/dashboard'

defineOptions({ name: 'MerchantO2oDashboard' })

const router = useRouter()
const loading = ref(false)
const summary = ref<O2oMerchantDashboardSummaryVO>({})
const availability = ref<any>({})
const providerRank = ref<O2oMerchantDashboardProviderRankVO[]>([])

const statCards = computed(() => {
  const s = summary.value
  return [
    {
      key: 'month',
      title: '本月 GMV',
      value: `¥${fmtMoney(s.monthGmv)}`,
      extra: `${s.monthOrderCount ?? 0} 单`,
      icon: 'ep:money',
      color: '#409eff',
      bg: 'rgba(64,158,255,0.1)',
      path: '/store/o2o/finance'
    },
    {
      key: 'total',
      title: '累计订单',
      value: s.totalOrderCount ?? 0,
      extra: `本月完成 ${s.monthCompletedCount ?? 0} 单`,
      icon: 'ep:document',
      color: '#6366f1',
      bg: 'rgba(99,102,241,0.1)',
      path: '/store/o2o/order'
    },
    {
      key: 'score',
      title: '平均好评',
      value: fmtScore(s.avgReviewScore),
      extra: '基于已发布评价',
      icon: 'ep:star',
      color: '#e6a23c',
      bg: 'rgba(230,162,60,0.1)',
      path: '/store/o2o/review'
    },
    {
      key: 'pending',
      title: '待内派',
      value: s.pendingAssignCount ?? 0,
      extra: `进行中 ${s.processingOrderCount ?? 0} 单`,
      icon: 'ep:position',
      color: '#f56c6c',
      bg: 'rgba(245,108,108,0.1)',
      path: '/store/o2o/order'
    },
    {
      key: 'today',
      title: '今日订单',
      value: s.todayOrderCount ?? 0,
      extra: `GMV ¥${fmtMoney(s.todayGmv)}`,
      icon: 'ep:calendar',
      color: '#67c23a',
      bg: 'rgba(103,194,58,0.1)',
      path: '/store/o2o/order'
    },
    {
      key: 'provider',
      title: '活跃师傅',
      value: s.activeProviderCount ?? 0,
      icon: 'ep:user',
      color: '#909399',
      bg: 'rgba(144,147,153,0.1)',
      path: '/store/o2o/provider'
    }
  ]
})

const trendChartOptions = reactive<EChartsOption>({
  tooltip: { trigger: 'axis' },
  legend: { data: ['GMV（元）', '订单数'], top: 0 },
  grid: { left: 50, right: 50, top: 40, bottom: 30 },
  xAxis: { type: 'category', data: [] as string[], boundaryGap: false },
  yAxis: [
    { type: 'value', name: 'GMV', splitLine: { lineStyle: { type: 'dashed' } } },
    { type: 'value', name: '订单', splitLine: { show: false } }
  ],
  series: [
    {
      name: 'GMV（元）',
      type: 'line',
      smooth: true,
      data: [] as number[],
      itemStyle: { color: '#409eff' },
      areaStyle: { opacity: 0.08 }
    },
    {
      name: '订单数',
      type: 'line',
      smooth: true,
      yAxisIndex: 1,
      data: [] as number[],
      itemStyle: { color: '#67c23a' },
      areaStyle: { opacity: 0.06 }
    }
  ]
})

function fmtMoney(v?: number | null) {
  if (v == null || Number.isNaN(Number(v))) return '0.00'
  return Number(v).toFixed(2)
}

function fmtScore(v?: number | null) {
  if (v == null || Number(v) <= 0) return '—'
  return Number(v).toFixed(1)
}

function goTo(path?: string) {
  if (path) router.push(path)
}

async function loadAll() {
  loading.value = true
  try {
    const [sum, trend, rank, availabilityStats] = await Promise.all([
      getO2oMerchantDashboardSummary(),
      getO2oMerchantDashboardTrend(),
      getO2oMerchantDashboardProviderRank(10),
      getO2oMerchantAvailabilityStats()
    ])
    summary.value = sum || {}
    providerRank.value = rank || []
    availability.value = availabilityStats || {}
    trendChartOptions.xAxis!.data = trend?.dates || []
    ;(trendChartOptions.series as any[])[0].data = trend?.gmv || []
    ;(trendChartOptions.series as any[])[1].data = trend?.orderCount || []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadAll()
})
</script>

<style scoped lang="scss">
.o2o-merchant-dashboard {
  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }

  .subtitle {
    margin: 4px 0 0;
    color: var(--el-text-color-secondary);
    font-size: 13px;
  }

  .stat-card {
    cursor: pointer;
    transition: transform 0.15s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .stat-content {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .stat-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 12px;
  }

  .stat-value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
  }

  .stat-label {
    margin-top: 4px;
    color: var(--el-text-color-secondary);
    font-size: 13px;
  }

  .stat-extra {
    margin-top: 2px;
    color: var(--el-text-color-placeholder);
    font-size: 12px;
  }
}
</style>
